import React from 'react';
import { Trash2, Plus, Minus } from 'lucide-react';
import { CartItem as CartItemType } from '../../types/cart';
import { formatCurrency } from '../../utils/format';

interface CartItemProps {
  item: CartItemType;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemove: (productId: string) => void;
}

const CartItem: React.FC<CartItemProps> = ({ item, onUpdateQuantity, onRemove }) => {
  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-3 sm:p-4 bg-dark-dark dark:bg-white rounded-xl shadow-sm border border-dark-light dark:border-gray-100 animate-fade-in transition-colors duration-300">
      {/* Product Image */}
      <div className="w-full sm:w-24 h-48 sm:h-24 md:w-28 md:h-28 flex-shrink-0 rounded-lg overflow-hidden bg-dark-light dark:bg-gray-100 transition-colors duration-300">
        <img
          src={item.image}
          alt={item.name}
          className="w-full h-full object-cover"
        />
      </div>

      {/* Product Details */}
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <h3 className="font-semibold text-white dark:text-dark-dark text-base sm:text-lg transition-colors duration-300">
            {item.name}
          </h3>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-0.5 transition-colors duration-300">
            Unit: {item.unit}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-2 sm:mt-0">
          {/* Price */}
          <div className="flex flex-col">
            <span className="text-accent dark:text-primary font-bold text-lg transition-colors duration-300">
              {formatCurrency(item.price * item.quantity)}
            </span>
            <span className="text-gray-400 dark:text-gray-500 text-sm transition-colors duration-300">
              {formatCurrency(item.price)} each
            </span>
          </div>

          {/* Quantity Controls */}
          <div className="flex items-center justify-between sm:justify-end gap-3">
            <div className="flex items-center border border-dark dark:border-gray-300 rounded-lg transition-colors duration-300">
              <button
                onClick={() => onUpdateQuantity(item.productId, item.quantity - 1)}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300 disabled:opacity-50"
                disabled={item.quantity <= 1}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 sm:w-10 text-center font-medium text-white dark:text-dark-dark transition-colors duration-300">
                {item.quantity}
              </span>
              <button
                onClick={() => onUpdateQuantity(item.productId, item.quantity + 1)}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300 disabled:opacity-50"
                disabled={item.quantity >= item.maxQuantity}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>

            {/* Remove Button */}
            <button
              onClick={() => onRemove(item.productId)}
              className="p-2 text-red-500 hover:bg-red-900/30 dark:hover:bg-red-100 rounded-lg transition-colors duration-300"
              aria-label="Remove item"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;